import { useGetTopics } from '@/hooks/api/dashboard'
import useAppStore from '@/store/useAppStore'
import { Topic, TopicListFilter } from '@/types'
import { Box, Grid, MenuItem, Select, SelectChangeEvent, Typography, styled } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { shallow } from 'zustand/shallow'
import TopicItem from './TopicItem'
import { LoadingButton } from '@mui/lab'
import { NoResults, ModernCardSkeleton, PatternCard, AlbumCard } from '@/components'
import { useNavigate, useLocation } from 'react-router-dom'
import { PrivacyType, TopicType, TopicTypes } from '@/utils/constants'
import DiscussionTopicItem from './DiscussionTopicItem'
import { CreateTopicModalContext } from '@/context/CreateTopicModalContext'
import TopicGridItem from './TopicGridItem'
import ModernTopicCard from './ModernTopicCard'
// import HeroSection from '../components/HeroSection'

const filterTopicTypes: TopicType[] = [TopicType.PATTERN, TopicType.ALBUM]

const RecentDesignsContainer = styled(Box)({
  backgroundColor: '#ffffff',
  padding: '20px 20px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: '24px',
  fontFamily: 'Inter, sans-serif',
})

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
})

const CountText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 400,
  color: '#6b7280',
  fontFamily: 'Inter, sans-serif',
})

const FilterSelect = styled(Select)({
  height: '40px',
  borderRadius: '8px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
  minWidth: '180px',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover': {
    backgroundColor: '#f3f4f6',
  },

  '&.Mui-focused': {
    backgroundColor: '#ffffff',
    borderColor: '#8b5cf6',
    boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
  },
})

export default function HomeSearchResult() {
  const [page, setPage] = useState(1)
  const [endReached, setEndReached] = useState(false)
  const location = useLocation()
  const { setIsPrivacySelected, updateTopicSelectedAt } = useAppStore()
  const [interactionType, topicType, topicList] = useAppStore(
    (state) => [state.home.interactionFilterType, state.home.topicFilterType, state.home.topicList],
    shallow,
  )
  const { contextTopicType, contextPrivacyType, onClickTopic, openModal, isDiscover } =
    useContext(CreateTopicModalContext)
  const [selectedTopicType, setSelectedTopicType] = useState<TopicType>(contextTopicType!)

  // Dynamic page title based on route and context
  const pageTitle = useMemo(() => {
    const pathname = location.pathname

    // Workspace routes (private content)
    if (pathname.startsWith('/workspace/')) {
      switch (pathname) {
        case '/workspace/all':
          return 'My Workspace'
        case '/workspace/pattern':
          return 'My Patterns'
        case '/workspace/album':
          return 'My Albums'
        case '/workspace/gallery':
          return 'My Galleries'
        case '/workspace/custom_print':
          return 'My Custom Prints'
        case '/workspace/upload':
          return 'My Uploads'
        default:
          return 'My Workspace'
      }
    }

    // Discover routes (public content)
    if (pathname.startsWith('/discover/')) {
      switch (pathname) {
        case '/discover/home':
          return 'Recent Designs'
        case '/discover/pattern':
          return 'Patterns'
        case '/discover/album':
          return 'Albums'
        case '/discover/gallery':
          return 'Galleries'
        case '/discover/custom_print':
          return 'Custom Prints'
        default:
          return 'Recent Designs'
      }
    }

    // Project routes
    if (pathname.startsWith('/projects/')) {
      switch (pathname) {
        case '/projects/quilt':
          return 'My Quilts'
        case '/projects/gallery':
          return 'My Project Galleries'
        default:
          return 'My Projects'
      }
    }

    // Default routes
    if (pathname === '/' || pathname === '/home' || pathname === '/topic') {
      return 'Recent Designs'
    }

    return 'Recent Designs'
  }, [location.pathname])

  const getTopics = useGetTopics()
  const navigate = useNavigate()

  const handleFilterChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string
    const type = TopicTypes[value].type
    setSelectedTopicType(type)
  }

  const scrollRef = useRef<any>(null)

  useEffect(() => {
    setEndReached(false)
    loadTopics(1)
  }, [selectedTopicType])

  useEffect(() => {
    if (page === 1 && getTopics.isSuccess) {
      setTimeout(() => {
        if (scrollRef?.current) {
          scrollRef?.current.scrollTo({ top: 0, left: 0 })
        }
      }, 1000)
    }
  }, [page, getTopics.isSuccess])

  useEffect(() => {
    if (getTopics.isSuccess && getTopics.data && !getTopics.isError) {
      if (getTopics.data.length < 20) {
        setEndReached(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTopics.isSuccess, getTopics.data, getTopics.isError])

  const topics = useMemo(() => {
    const list = Object.values(topicList)
    // sort by date updated
    list.sort((a, b) => {
      if (a.dateUpdated > b.dateUpdated) return -1
      if (a.dateUpdated < b.dateUpdated) return 1
      return 0
    })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicList])

  useEffect(() => {
    if (getTopics.isSuccess && page === 1) {
      updateTopicSelectedAt()
    }
  }, [getTopics.isSuccess, getTopics.data, page])

  const onClickShowMore = () => {
    if (endReached || getTopics.isLoading || !topics.length) {
      return
    }
    loadTopics(page + 1)
    setPage(page + 1)
  }

  const loadTopics = (pageNo: number = 1) => {
    getTopics.mutate({
      pageNo,
      interaction: Number(contextPrivacyType),
      topicType: Number(selectedTopicType),
    })
  }

  const onRemovedTopic = () => {
    // navigate('/topic')
  }

  const onClickTopicItem = (topic: Topic) => {
    setIsPrivacySelected(false)
    onClickTopic(topic)
  }

  const onClickNew = () => {
    openModal(selectedTopicType, contextPrivacyType)
  }

  const showNoResultView = useMemo(() => {
    return getTopics.isSuccess && getTopics.data.length == 0 && page === 1
  }, [getTopics.isSuccess, getTopics.data])

  const handleSearch = (query: string) => {
    // TODO: Implement search functionality
    console.log('Search query:', query)
  }

  const handleCategoryClick = (categoryId: string) => {
    // TODO: Implement category filtering
    console.log('Category clicked:', categoryId)
  }

  return (
    <Box flex={1} flexDirection={'column'} ref={scrollRef}>
      {/* Hero Section */}
      {/* <HeroSection onSearch={handleSearch} onCategoryClick={handleCategoryClick} /> */}

      {/* Recent Designs Section */}
      <RecentDesignsContainer>
        <SectionTitle>{pageTitle}</SectionTitle>

        <HeaderRow>
          {getTopics.isLoading ? (
            <CountText>Loading...</CountText>
          ) : (
            <CountText>
              <span style={{ color: '#1a1a1a', fontWeight: 600 }}>{topics.length}</span>{' '}
              {`${
                selectedTopicType == TopicType.ALL
                  ? 'designs'
                  : `${TopicTypes[selectedTopicType].title.toLowerCase()}(s)`
              }`}
            </CountText>
          )}

          {contextTopicType == TopicType.ALL && (
            <FilterSelect
              value={`${selectedTopicType}`}
              onChange={handleFilterChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Filter designs' }}
            >
              <MenuItem value={'0'}>All design types</MenuItem>
              {filterTopicTypes.map((item, index) => {
                return (
                  <MenuItem key={`${index}`} value={`${item}`}>
                    {TopicTypes[item].title}
                  </MenuItem>
                )
              })}
            </FilterSelect>
          )}
        </HeaderRow>

        {getTopics.isLoading && page === 1 && (
          <ModernCardSkeleton count={12} showActions={true} showStats={true} />
        )}

        {showNoResultView ? (
          <Box py={8} display='flex' justifyContent={'center'}>
            <NoResults interaction={interactionType} topicType={topicType} onClick={onClickNew} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {topics.map((item: Topic, index: number) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.topicID}-${index}`}>
                {item.topicType == TopicType.DISCUSSION.toString() ? (
                  <DiscussionTopicItem
                    topic={item}
                    key={item.topicID}
                    onClick={() => onClickTopicItem(item)}
                    onRemoved={onRemovedTopic}
                  />
                ) : item.topicType == TopicType.PATTERN.toString() ? (
                  <PatternCard topic={item} onClick={() => onClickTopicItem(item)} />
                ) : item.topicType == TopicType.ALBUM.toString() ? (
                  <AlbumCard topic={item} onClick={() => onClickTopicItem(item)} />
                ) : (
                  <ModernTopicCard
                    topic={item}
                    key={item.topicID}
                    onClick={() => onClickTopicItem(item)}
                    onRemoved={onRemovedTopic}
                    showStats={true}
                    showActions={true}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}

        {(page > 1 || !getTopics.isLoading) && (
          <Box mt={4} display='flex' justifyContent={'center'}>
            <LoadingButton
              variant='outlined'
              onClick={onClickShowMore}
              loading={getTopics.isLoading && page > 1}
              disabled={endReached}
              sx={{
                borderColor: '#e5e7eb',
                color: '#6b7280',
                fontWeight: 500,
                padding: '12px 24px',
                borderRadius: '8px',

                '&:hover': {
                  borderColor: '#8b5cf6',
                  backgroundColor: 'rgba(139, 92, 246, 0.04)',
                  color: '#8b5cf6',
                },
              }}
            >
              {endReached ? 'No more designs' : 'Show more designs'}
            </LoadingButton>
          </Box>
        )}
      </RecentDesignsContainer>
    </Box>
  )
}
